'use client';

import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { extractSpaceIdFromPathname } from '@/constants/routes';
import { useCurrentSpace } from '@/stores/space';

/**
 * One-way sync from URL path to Zustand space store.
 *
 * Uses usePathname() (not useParams()) because window.history.replaceState()
 * integrates with usePathname but NOT useParams. This ensures the store stays
 * in sync when the space switcher updates the URL without a full navigation.
 */
export default function useSpaceUrlSync(): void {
  const pathname = usePathname();
  const urlSpaceId = extractSpaceIdFromPathname(pathname);
  const { spaceId: storeSpaceId, setSpace } = useCurrentSpace();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (urlSpaceId === '') {
      return;
    }

    if (urlSpaceId !== storeSpaceId) {
      setSpace(urlSpaceId);

      queryClient.invalidateQueries({ queryKey: ['courseById'] });
      queryClient.invalidateQueries({ queryKey: ['sequenceById'] });
      queryClient.invalidateQueries({ queryKey: ['quizById'] });
      queryClient.invalidateQueries({ queryKey: ['userRole'] });
      queryClient.invalidateQueries({ queryKey: ['spaceCode'] });
    }
  }, [urlSpaceId, storeSpaceId, setSpace, queryClient]);
}
